import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { useUser } from '@auth_module/hooks/useUser';
import { UserApplication, logout } from '@auth_module/services';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useGroupParams } from '@insights_module/hooks/useGroupParams';
import { useGarmParams } from '@insights_module/hooks/useGarmParams';
import useOnClickOutside from '@app/hooks/useOnClickOutside';

import { Home } from '@icons/Home';
import { HelpIcon } from '@icons/HelpIcon';
import { Logout } from '@icons/Logout';
import { SunIcon } from '@icons/SunIcon';
import { CreditCardIcon } from '@icons/CreditCardIcon';
import { Loader } from '@components/Loader';
import { ThemeToggler } from '@themes/ThemeToggler';
import { useFeatureFlag, FEATURE_FLAG } from '@auth_module/hooks/useFeatureFlag';
import { EditAvatarModal } from '@components/EditAvatarModal';
import { EditIcon } from '@icons/EditIcon';
import { useAvatar } from '@app/hooks/useAvatar';

const Avatar = tw.div`relative w-9 h-9 rounded-full bg-accent-2 cursor-pointer flex items-center justify-center z-[100]`;
const AvatarMenuWrapper = tw.div`absolute top-[40px] right-0 flex flex-col bg-transparent z-20 shadow-dropdown`;
const AvatarMenu = tw.div`flex flex-col w-[220px] bg-neutral-background border border-gray-1 rounded z-20 p-1`;
const MenuButton = tw.div`flex flex-row items-center justify-start w-full py-[1.2rem] pl-[0.4rem] rounded-[3px] h-[25px] text-[11px] text-text-and-icon-muted font-semibold cursor-pointer hover:bg-gray-2`;
const Details = tw.div`bg-gray-2 flex items-center justify-start text-left -m-1 mb-1 p-2 border-b border-solid border-gray-1`;
const Separator = tw.div`h-[1px] w-full bg-gray-1 my-1`;
const UserName = tw.p`text-sm text-text-and-icon-muted text-left font-semibold truncate`;
const UserEmail = tw.p`text-xs text-text-and-icon text-left truncate`;
const AvatarImage = tw.img`bg-accent-2 rounded-full w-full h-full`;
const EditAvatarWrapper = tw.div`relative`;
const EditAvatarImage = tw.img`bg-accent rounded-full min-w-[32px] min-h-[32px] mr-2 transition-all duration-[200ms]`;
const EditAvatarIcon = tw(
  EditIcon
)`mr-1 w-4 stroke-text-and-icon-muted z-[100] transition-all duration-[200ms] rounded-full p-0.5 bg-neutral-background opacity-0 group-hover:opacity-100`;

export const UserAvatar = ({
  currentApplication,
  ...rest
}: {
  currentApplication: UserApplication | null;
}) => {
  const { t } = useTranslation();
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const [isEditAvatarModalVisible, setIsEditAvatarModalVisible] = useState<boolean>(false);
  const navigate = useNavigate();
  const { user, userApplications } = useUser();
  const { isEnabled: isThemeTogglerEnabled } = useFeatureFlag(FEATURE_FLAG.THEME_TOGGLER);
  const { isEnabled: isEditUserAvatarEnabled } = useFeatureFlag(FEATURE_FLAG.EDIT_USER_AVATAR);
  const { userAvatar, isLoadingUserAvatar } = useAvatar();

  const avatarMenuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(avatarMenuRef, () => setIsMenuVisible(false));

  const { setGroupParams } = useGroupParams();
  const { setAllGarmParams } = useGarmParams();

  // const { setIsWhatsNewVisible } = useAccountContext();
  const { setSelectedShow, setSelectedEpisode: setSelectedInsightsEpisode } = useInsightsContext();

  const handleLogout = async () => {
    await logout();
  };

  const handleGoToDashboard = () => {
    if (currentApplication === 'audio-insights') {
      setAllGarmParams(undefined);
      setGroupParams({ name: undefined });
      setSelectedShow('');
      setSelectedInsightsEpisode('');
      navigate('/insights');
    }

    setIsMenuVisible(false);
  };

  // const handleOpeWhatsNew = () => {
  //   setIsWhatsNewVisible(true);
  //   setIsMenuVisible(false);
  // };

  const handleHelpClick = () => {
    window.open('https://help.tritondigital.com/docs/sounder', '_blank');
    setIsMenuVisible(false);
  };

  return (
    <>
      <Avatar ref={avatarMenuRef} {...rest}>
        {isLoadingUserAvatar ? (
          <Loader size='sm' theme='dark' css={tw`border-text-and-icon-invert `} />
        ) : (
          <AvatarImage
            src={userAvatar?.url}
            alt='user-avatar'
            onClick={() => setIsMenuVisible(!isMenuVisible)}
          />
        )}

        {isMenuVisible && (
          <AvatarMenuWrapper>
            <AvatarMenu>
              <Details>
                <EditAvatarWrapper
                  className='group'
                  onClick={() => {
                    setIsMenuVisible(false);

                    isEditUserAvatarEnabled &&
                      setIsEditAvatarModalVisible(!isEditAvatarModalVisible);
                  }}>
                  <EditAvatarImage src={userAvatar.url} alt='user-avatar' />
                  {isEditUserAvatarEnabled && (
                    <button title='Change avatar' tw='absolute bottom-0 right-0'>
                      <EditAvatarIcon />
                    </button>
                  )}
                </EditAvatarWrapper>
                <div tw='overflow-hidden'>
                  <UserName>
                    {user?.first_name} {user?.last_name}
                  </UserName>
                  <UserEmail>{user?.email}</UserEmail>
                </div>
              </Details>
              <MenuButton onClick={() => handleGoToDashboard()}>
                <Home tw='stroke-text-and-icon-muted align-middle mr-1 mt-[-1px] w-6' />
                {t('common.dashboard')}
              </MenuButton>

              {/* {userApplications && userApplications.includes('promohub') && (
                <MenuButton onClick={handleGoToBilling}>
                  {isPortalLoading ? (
                    <div tw='max-w-[18px] max-h-[18px] mr-[10px]'>
                      <Loader theme='dark' size='sm' />
                    </div>
                  ) : (
                    <CreditCardIcon tw='stroke-text-and-icon-muted align-middle mr-1 mt-[-1px] w-6' />
                  )}
                  Billing
                </MenuButton>
              )} */}

              {/* Temporarily hide whats new button*/}
              {/* <MenuButton onClick={handleOpeWhatsNew}>
              <NewspaperIcon tw='stroke-text-and-icon-muted align-middle mr-1 w-6' />
              {t('insights.header.whats_new')}
            </MenuButton> */}

              {isThemeTogglerEnabled && (
                <MenuButton tw='pr-[4px]'>
                  <div tw='w-full flex items-center justify-between'>
                    <div tw='flex items-center justify-start'>
                      <SunIcon tw='fill-text-and-icon-muted align-middle mr-1 w-6' />
                      {t('common.theme')}
                    </div>
                    <ThemeToggler />
                  </div>
                </MenuButton>
              )}

              <MenuButton onClick={handleHelpClick}>
                <HelpIcon tw='stroke-text-and-icon-muted align-middle mr-1 w-6' />
                {t('common.help')}
              </MenuButton>

              <div tw='px-1'>
                <Separator />
              </div>
              <MenuButton onClick={() => handleLogout()}>
                <Logout tw='fill-text-and-icon-muted align-middle mr-1 w-6' />
                {t('insights.header.logout')}
              </MenuButton>
            </AvatarMenu>
          </AvatarMenuWrapper>
        )}
      </Avatar>
      {isEditAvatarModalVisible && (
        <EditAvatarModal onClose={() => setIsEditAvatarModalVisible(false)} />
      )}
    </>
  );
};
